import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CredentialsService } from '@app/modules/authentication/credentials.service';
import { ILocationSetting } from "@app/modules/shift-types/models/location-setting/ilocation-setting";
import { environment } from '@env/environment';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CitiesService, ICity } from './cities.service';
import { BaseStore } from './store-pattern/base-store.service';
export interface ICountry {
  name?: string;
  code?: string;
  id?: number;
  dial_code?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CountriesService extends BaseStore<ICountry[]> {
  static countrySettings = {
    defaultCountryCode: environment.defaultCountryCode,
    ...environment.countrySettings,
  };

  public static setLocationSetting(setting: ILocationSetting) {
    this.countrySettings.defaultLatitude = setting.lat;
    this.countrySettings.defaultLongitude = setting.lng;
  }

  static setSettings(
    country: ICountry,
    cities: ICity[],
    object: any = environment.countrySettings
  ) {
    this.countrySettings = {
      ...object,
      defaultCountryCode: country.code?.toLowerCase() ?? 'sa',
      country: country.name ?? object.country ?? 'Saudi Arabia',
      countryCode: country.dial_code ?? object.countryCode ?? '+966',
      cities,
      defaultLatitude: this.countrySettings.defaultLatitude,
      defaultLongitude: this.countrySettings.defaultLongitude
    };
  }

  constructor(httpClient: HttpClient, private citiesService: CitiesService,
     credentialsService: CredentialsService,
    ) {
    super(httpClient, credentialsService, {
      baseUrl: '/org/merchant/PickupLocation',
      getUrl: '/countries',
    });
  }

  get location(): ILocationSetting {
    return {
      lat: CountriesService.countrySettings.defaultLatitude,
      lng: CountriesService.countrySettings.defaultLongitude,
    };
  }

  setLocation(setting: ILocationSetting,
              callback: Function = (() => {})) {
    CountriesService.setLocationSetting(setting);
    callback();
  }  private static initPromise: Promise<void> | null = null;
  private static countriesCache: ICountry[] | null = null;
  // Track which country codes we've already set up
  private static initializedCountryCodes = new Set<string>();
  async setDefaultCountry(countryCode: string = environment.defaultCountryCode) {
    // Normalize the country code to ensure consistent handling
    const defaultCountryCode = (countryCode).toLowerCase();
    
    // Skip if this country code is already initialized
    if (CountriesService.initializedCountryCodes.has(defaultCountryCode)) {
      return;
    }

    // Initialize only once
    if (!CountriesService.initPromise) {
      CountriesService.initPromise = this.init();
    }
    await CountriesService.initPromise;

    // Use cached countries data if available
    if (!CountriesService.countriesCache) {
      CountriesService.countriesCache = await firstValueFrom(this.loadDataAnonymously());
      this.data = CountriesService.countriesCache;
    }
    
    try {
      // Only fetch cities when we have countries data
      if (CountriesService.countriesCache) {
        // Use the cached cities observable from CitiesService
        const cities = await firstValueFrom(this.citiesService.getByCountryCode(defaultCountryCode));
        const country = CountriesService.countriesCache.find((c) => c.code?.toLowerCase() === defaultCountryCode);
        
        if (country) {
          CountriesService.setSettings(country, cities ?? []);
          // Mark this country code as initialized
          CountriesService.initializedCountryCodes.add(defaultCountryCode);
          console.log(`Country ${defaultCountryCode} initialized successfully`);
        } else {
          console.warn(`Country ${defaultCountryCode} not found in countries data`);
        }
      }
    } catch (error) {
      console.error(`Error setting default country ${defaultCountryCode}:`, error);
    }
  }
  /**
   * Preloads countries data to avoid multiple API calls
   * Should be called once during app initialization
   */
  preloadCountries(): Observable<ICountry[]> {
    // Use the loadDataAnonymously method which now has proper caching
    return this.loadDataAnonymously().pipe(
      map(countries => {
        CountriesService.countriesCache = countries;
        this.data = countries;
        this.initiated = true;
        return countries;
      })
    );
  }
}
